<script setup lang="ts">
  import { Skeleton, Typo, TypoVariant } from '@travelata-libs/ui-kit';
  import { FieldSize } from '../model/types';

  type FieldVariant = 'input' | 'display';

  interface FieldProps {
    label: string;
    active?: boolean;
    disabled?: boolean;
    variant?: FieldVariant;
    valueTestid?: string;
    size?: `${FieldSize}`;
    loading?: boolean;
    error?: boolean;
  }

  const props = withDefaults(defineProps<FieldProps>(), {
    active: false,
    disabled: false,
    variant: 'display',
    valueTestid: undefined,
    size: FieldSize.M,
    loading: false,
    error: false,
  });

  interface FieldEmits {
    (e: 'click', event: MouseEvent): void;
  }

  const emit = defineEmits<FieldEmits>();

  const onClick = (event: MouseEvent): void => {
    if (props.loading || props.disabled) {
      return;
    }
    emit('click', event);
  };
</script>

<template>
  <div
    class="field__control"
    :class="[
      `field__control_${props.variant}`,
      `field__control_size-${size}`,
      {
        field__control_active: active,
        field__control_disabled: disabled,
        field__control_loading: loading,
        field__control_error: error,
      },
    ]"
    @click="onClick"
  >
    <Typo
      class="field__label"
      :variant="TypoVariant.BodySRegular"
      tag="span"
    >
      {{ label }}
    </Typo>

    <div
      class="field__value"
      :data-testid="valueTestid"
    >
      <slot name="value" />
    </div>

    <span
      v-if="loading"
      class="field__skeleton"
      aria-hidden="true"
      data-testid="field-skeleton"
    >
      <Typo
        class="field__skeleton-row"
        :variant="TypoVariant.BodySRegular"
        tag="span"
      >
        &#8203;
        <Skeleton
          class="field__plate"
          width="var(--field-skeleton-label-width)"
          height="12px"
        />
      </Typo>
      <Typo
        class="field__skeleton-row"
        :variant="TypoVariant.BodyLRegular"
        tag="span"
      >
        &#8203;
        <Skeleton
          class="field__plate"
          width="var(--field-skeleton-value-width)"
          height="14px"
        />
      </Typo>
    </span>
  </div>
</template>

<style scoped lang="scss">
  $skeleton-shimmer-width: 240px;

  .field {
    $root: &;

    &__control {
      background: transparent;
      display: flex;
      flex-direction: column;
      gap: var(--spacing-xxxs);
      justify-content: space-between;
      padding: var(--spacing-xs) var(--spacing-s);
      position: relative;
      transition: background-color var(--transition-default);

      &_input {
        cursor: text;

        // Строчный инпут добавлял полпикселя высоты и под рамкой ошибки поля дат проступала белая полоска
        #{$root}__value {
          display: flex;
        }
      }

      &_display {
        box-sizing: border-box;
        cursor: pointer;
        height: 54px;
        justify-content: space-between;

        #{$root}__value {
          color: var(--color-text-primary);
          white-space: nowrap;
        }
      }

      &_size-l {
        gap: var(--spacing-xxs);
        padding: var(--spacing-s);

        &#{$root}__control_display {
          height: 64px;
        }
      }

      &_disabled {
        background: var(--color-disabled-100);

        #{$root}__label {
          color: var(--color-disabled-900);
        }

        #{$root}__value {
          color: var(--color-text-tertiary);
        }
      }

      &_error {
        background: var(--color-bg-primary);
        box-shadow: inset 0 0 0 1px var(--color-error-500);

        #{$root}__label,
        #{$root}__value {
          color: var(--color-error-500);
        }
      }

      &_loading {
        #{$root}__label,
        #{$root}__value {
          opacity: 0;
        }
      }
    }

    &__label {
      color: var(--color-text-secondary);
      pointer-events: none;
      user-select: none;
    }

    &__skeleton {
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      gap: inherit;
      inset: 0;
      justify-content: space-between;
      padding: inherit;
      pointer-events: none;
      position: absolute;
    }

    &__skeleton-row {
      display: block;
      position: relative;
    }

    // Без transform: с ним Chrome ломает fixed-фон блика.
    &__plate {
      inset: 0 auto;
      margin: auto 0;
      position: absolute;
    }

    &__skeleton #{$root}__plate {
      animation: field-skeleton-shimmer 1.6s linear infinite;
      background: var(--color-neutral-20) linear-gradient(90deg, transparent, var(--color-bg-primary), transparent)
        no-repeat fixed;
      background-size: $skeleton-shimmer-width 100%;

      @media (prefers-reduced-motion: reduce) {
        animation: field-skeleton-pulse 2s ease-in-out infinite;
        background-image: none;
      }
    }
  }

  @keyframes field-skeleton-shimmer {
    from {
      background-position: -$skeleton-shimmer-width 0;
    }

    to {
      background-position: calc(100vw + #{$skeleton-shimmer-width}) 0;
    }
  }

  @keyframes field-skeleton-pulse {
    50% {
      opacity: 0.6;
    }
  }
</style>
